import { logger } from '@inboundx/shared/platform';
import type { SupabaseClient } from '@supabase/supabase-js';

import type { ProductUpdate } from '@/domain/productUpdates';
import { supabase } from '@/lib/supabase';

// ponytail: backoffice_product_updates / backoffice_product_update_reads aren't
// in the generated Database types yet (migration not applied), so read them
// through an untyped client. Drop this once `just gen-types` includes the tables.
const db = supabase as unknown as SupabaseClient;

// Only surface the latest few weeks — the bell is "what's new", not an archive.
const RECENT_UPDATES_LIMIT = 3;

/** The most recent product updates, newest first. */
export async function fetchProductUpdates(): Promise<ProductUpdate[]> {
    const { data, error } = await db
        .from('backoffice_product_updates')
        .select('id, week_start, title, body_md, published_at')
        .order('week_start', { ascending: false })
        .limit(RECENT_UPDATES_LIMIT);

    if (error) {
        logger.error('Error fetching product updates:', error);
        throw error;
    }
    return (data ?? []) as ProductUpdate[];
}

/** The user's last-seen time, or null if they've never opened the panel. */
export async function fetchLastSeen(userId: string): Promise<string | null> {
    const { data, error } = await db
        .from('backoffice_product_update_reads')
        .select('last_seen_at')
        .eq('user_id', userId)
        .maybeSingle();

    if (error) {
        logger.error('Error fetching product-update last-seen:', error);
        throw error;
    }
    return (data?.last_seen_at as string | undefined) ?? null;
}

/** Mark all current updates as seen for this user. */
export async function markSeen(userId: string): Promise<void> {
    const { error } = await db
        .from('backoffice_product_update_reads')
        .upsert(
            { user_id: userId, last_seen_at: new Date().toISOString() },
            { onConflict: 'user_id' }
        );

    if (error) {
        logger.error('Error marking product updates seen:', error);
        throw error;
    }
}
